/**
 Copyright Highway9 Networks Inc.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "~/store";
import { defaultEdge, Edge, EdgeIpsecConfig } from "~/types/edge";
import { SNSSAI } from "~/types/OperatorQuery";
import { DEFAULT_SNSSAI_SST } from "~/constants";

type initState = {
  editState: Edge;
  isSecondaryAAA: boolean;
  isNetworkModified: Record<number, boolean>;
  isFabricNetworkModified: Record<string, boolean>;
};

const initialState: initState = {
  editState: defaultEdge,
  isSecondaryAAA: false,
  isNetworkModified: {},
  isFabricNetworkModified: {},
};
const edgeEditSlice = createSlice({
  name: "edgeEdit",
  initialState,
  reducers: {
    setEditState: (state, action: PayloadAction<Edge>) => {
      state.editState = action.payload;
      state.isNetworkModified = {};
      state.isFabricNetworkModified = {};
      state.isSecondaryAAA = (action.payload.authZConfig?.aaaServerProfiles?.length ?? 0) > 1;
    },
    // general
    setName: (state, action: PayloadAction<string>) => {
      state.editState.name = action.payload;
    },

    setFabricName: (state, action: PayloadAction<{ index: number; name: string }>) => {
      const { index, name } = action.payload;


      if (!state.editState.fabrics) {
        state.editState.fabrics = { fabricInterfaces: [{ fabricName: "", networks: [] }] };
      } else if (!state.editState.fabrics.fabricInterfaces.length || !state.editState.fabrics.fabricInterfaces[index]) {
        //interface is empty or index not present
        state.editState.fabrics.fabricInterfaces[index] = { fabricName: "", networks: [] };
      }
      // Set the fabric name
      state.editState.fabrics.fabricInterfaces[index].fabricName = name;
    },
    setInterfaceCount: (state, action: PayloadAction<number>) => {
      state.editState.interfaceCount = action.payload;
    },
    setEnableSecurityGateway: (state, action: PayloadAction<boolean>) => {
      state.editState.enableSecurityGateway = action.payload;
    },
    setNetworkDnnId: (state, action: PayloadAction<{ fabricIndex: number; networkIndex: number; dnnId: string }>) => {
      const { fabricIndex, networkIndex, dnnId } = action.payload;

      if (!state.editState.fabrics) {
        state.editState.fabrics = { fabricInterfaces: [{ fabricName: "", networks: [] }] };
      } else if (!state.editState.fabrics.fabricInterfaces.length || !state.editState.fabrics.fabricInterfaces[fabricIndex]) {
        //interface is empty or index not present
        state.editState.fabrics.fabricInterfaces[fabricIndex] = { fabricName: "", networks: [] };
      }

      // Initialize networks[networkIndex] if undefined
      if (!state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex]) {
        state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex] = {
          dnnId: "",
          vlanId: null,
          nativeVlan: false,
          gatewayIp: null,
          subnet: "192.168.128.0/24",
          ipAllocationMode: "DHCP",
        };
      }

      // Set dnnId
      state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex].dnnId = dnnId;

      const key = `${fabricIndex}-${networkIndex}`;
      state.isFabricNetworkModified[key] = true;
    },

    setNetworkNativeVlan: (state, action: PayloadAction<{ fabricIndex: number; networkIndex: number; nativeVlan: boolean; }>) => {
      const { fabricIndex, networkIndex, nativeVlan } = action.payload;

      // Initialize the fabric structure if missing
      if (!state.editState.fabrics) {
        state.editState.fabrics = {
          fabricInterfaces: [{ fabricName: "", networks: [] }],
        };
      } else if (
        !state.editState.fabrics.fabricInterfaces.length ||
        !state.editState.fabrics.fabricInterfaces[fabricIndex]
      ) {
        state.editState.fabrics.fabricInterfaces[fabricIndex] = {
          fabricName: "",
          networks: [],
        };
      }

      // Initialize the network entry if missing
      if (
        !state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex]
      ) {
        state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex] = {
          dnnId: "",
          vlanId: null,
          nativeVlan: false,
          gatewayIp: null,
          subnet: "192.168.128.0/24",
          ipAllocationMode: "DHCP",
        };
      }

      const network = state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex];
      // Update native VLAN flag
      network.nativeVlan = nativeVlan;
      if (nativeVlan) {
        network.vlanId = 0;
        network.gatewayIp = null;
      } else {
        network.vlanId = null;
        network.gatewayIp = null;
      }
      // Mark as modified
      const key = `${fabricIndex}-${networkIndex}`;
      state.isFabricNetworkModified[key] = true;
    },

    setNetworkGatewayIp: (
      state,
      action: PayloadAction<{ fabricIndex: number; networkIndex: number; gatewayIp: string }>
    ) => {
      const { fabricIndex, networkIndex, gatewayIp } = action.payload;

      if (!state.editState.fabrics) {
        state.editState.fabrics = { fabricInterfaces: [{ fabricName: "", networks: [] }] };
      } else if (!state.editState.fabrics.fabricInterfaces.length || !state.editState.fabrics.fabricInterfaces[fabricIndex]) {
        //interface is empty or index not present
        state.editState.fabrics.fabricInterfaces[fabricIndex] = { fabricName: "", networks: [] };
      }

      // Initialize networks[networkIndex] if undefined
      if (!state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex]) {
        state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex] = {
          dnnId: "",
          vlanId: null,
          gatewayIp: null,
          nativeVlan: false,
          subnet: "192.168.128.0/24",
          ipAllocationMode: "DHCP",
        };
      }

      // Set gatewayIp
      state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex].gatewayIp = gatewayIp;

      const key = `${fabricIndex}-${networkIndex}`;
      state.isFabricNetworkModified[key] = true;
    },

    setNetworkVlanId: (state, action: PayloadAction<{ fabricIndex: number; networkIndex: number; vlanId: number | string | null }>) => {
      const { fabricIndex, networkIndex, vlanId } = action.payload;

      // Initialize fabrics if undefined
      if (!state.editState.fabrics) {
        state.editState.fabrics = { fabricInterfaces: [{ fabricName: "", networks: [] }] };
      } else if (!state.editState.fabrics.fabricInterfaces.length || !state.editState.fabrics.fabricInterfaces[fabricIndex]) {
        //interface is empty or index not present
        state.editState.fabrics.fabricInterfaces[fabricIndex] = { fabricName: "", networks: [] };
      }

      // Initialize networks[networkIndex] if undefined
      if (!state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex]) {
        state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex] = {
          dnnId: "",
          ipAllocationMode: "DHCP",
          gatewayIp: null,
          nativeVlan: false,
          subnet: "192.168.128.0/24",
          vlanId: null,
        };
      }
      // Set vlanId
      state.editState.fabrics.fabricInterfaces[fabricIndex].networks[networkIndex].vlanId = vlanId;

      const key = `${fabricIndex}-${networkIndex}`;
      state.isFabricNetworkModified[key] = true;
    },

    deleteNetworkInterface: (state, action: PayloadAction<{ fabricIndex: number; networkIndex: number }>) => {
      const { fabricIndex, networkIndex } = action.payload;

      // Check if fabrics and fabricInterfaces exist
      if (
        state.editState.fabrics?.fabricInterfaces &&
        state.editState.fabrics.fabricInterfaces[fabricIndex]?.networks
      ) {
        // Filter out the network at the specified index
        state.editState.fabrics.fabricInterfaces[fabricIndex].networks = state.editState.fabrics.fabricInterfaces[
          fabricIndex
        ].networks.filter((_, index) => index !== networkIndex);
      }
    },

    addFabricNetwork: (state, action: PayloadAction<{ fabricIndex: number }>) => {
      const { fabricIndex } = action.payload;

      const newNetwork = {
        dnnId: "",
        vlanId: null,
        nativeVlan: false,
        gatewayIp: null,
        subnet: "192.168.128.0/24",
        ipAllocationMode: "DHCP",
      };

      // Ensure fabrics exists
      if (!state.editState.fabrics) {
        state.editState.fabrics = { fabricInterfaces: [] };
      }

      // Check if the fabric already exists, if not, initialize it
      if (!state.editState.fabrics.fabricInterfaces[fabricIndex]) {
        state.editState.fabrics.fabricInterfaces[fabricIndex] = { fabricName: "", networks: [] };
      }

      // Add the new network to the specified fabric's network interfaces
      state.editState.fabrics.fabricInterfaces[fabricIndex].networks.push(newNetwork);
    },


    // network
    setNetworkMode: (state, action: PayloadAction<string>) => {
      state.editState.networkMode = action.payload;
    },
    addNetwork: (state) => {
      state.editState.networks = [
        ...state.editState.networks,
        {
          dnnId: "",
          vlanId: null,
          nativeVlan: false,
          gatewayIp: null,
          subnet: "192.168.128.0/24",
          ipAllocationMode: "DHCP",
        },
      ];
    },

    setPrimaryDNS: (state, action: PayloadAction<string>) => {
      if (state.editState.managementNetwork) {
        state.editState.managementNetwork.dns[0] = action.payload;
      } else {
        state.editState.managementNetwork = {
          dns: action.payload?.length ? [action.payload] : [],
        };
      }
    },
    setSecondaryDNS: (state, action: PayloadAction<string>) => {
      if (state.editState.managementNetwork) {
        if (action.payload.length === 0) {
          state.editState.managementNetwork.dns.pop();
        } else {
          state.editState.managementNetwork.dns[1] = action.payload;
        }
      }
    },

    setDataNetworkPublicIp: (state, action: PayloadAction<string>) => {
      state.editState.dataNetworkPublicIp = action.payload;
    },
    setRadioNetworkPublicIp: (state, action: PayloadAction<string>) => {
      state.editState.radioNetworkPublicIp = action.payload;
    },
    setGlobalTcpMss: (state, action: PayloadAction<number>) => {
      state.editState.globalTcpMss = action.payload;
    },
    setLogLevelType: (state, action: PayloadAction<string>) => {
      state.editState.logLevelType = action.payload
    },
    deleteNetwork: (state, action: PayloadAction<{ index: number }>) => {
      state.editState.networks = state.editState.networks.filter((_, i) => action.payload.index !== i);
    },

    // network card

    setVlanId: (state, action: PayloadAction<{ value: number | string | null; index: number }>) => {
      state.editState.networks[action.payload.index].vlanId = action.payload.value;
      state.isNetworkModified[action.payload.index] = true;
    },
    setSubnet: (state, action: PayloadAction<{ value: string; index: number }>) => {
      state.editState.networks[action.payload.index].subnet = action.payload.value;
      state.isNetworkModified[action.payload.index] = true;
    },
    setDnn: (state, action: PayloadAction<{ value: string; index: number }>) => {
      state.editState.networks[action.payload.index].dnnId = action.payload.value;
      state.isNetworkModified[action.payload.index] = true;
    },
    setGatewayIP: (state, action: PayloadAction<{ value: string; index: number }>) => {
      state.editState.networks[action.payload.index].gatewayIp = action.payload.value;
      state.isNetworkModified[action.payload.index] = true;
    },
    setGlobalTCPMSS: (state, action: PayloadAction<number>) => {
      state.editState.globalTcpMss = action.payload;
    },
    resetNetworkModified: (state, action: PayloadAction<number>) => {
      delete state.isNetworkModified[action.payload]; // Reset for one index
    },

    // monitoring

    setSNMPMonitoring: (state, action: PayloadAction<{ id: string; default?: boolean }>) => {
      if (state.editState.monitoringSystemConfig) {
        state.editState.monitoringSystemConfig.id = action.payload.id;
        state.editState.monitoringSystemConfig.useDefault = action.payload.default ?? false;
      } else {
        state.editState.monitoringSystemConfig = {
          id: action.payload.id,
          useDefault: action.payload.default ?? false,
        };
      }
    },

    /// AAA
    setAuthzEnabled: (state, action: PayloadAction<boolean>) => {
      if (state.editState.authZConfig) {
        state.editState.authZConfig.authzEnabled = action.payload;
      } else {
        state.editState.authZConfig = {
          authzEnabled: action.payload,
          aaaServerProfiles: [],
          isAuthzConfigChanged: false,
        };
      }
    },

    setAAAProfile: (state, action: PayloadAction<string>) => {
      if (state.editState.authZConfig) {
        state.editState.authZConfig.aaaServerProfiles[0] = {
          aaaServerProfileId: action.payload,
        };
      }
    },
    setIsEnabledSecondaryAAAServer: (state, action: PayloadAction<boolean>) => {
      state.isSecondaryAAA = action.payload;
      if (!action.payload && state.editState.authZConfig && state.editState.authZConfig.aaaServerProfiles.length > 1) {
        state.editState.authZConfig.aaaServerProfiles = state.editState.authZConfig.aaaServerProfiles.slice(0, 1);
      }
    },

    setSecondaryAAAProfile: (state, action: PayloadAction<string>) => {
      if (state.editState.authZConfig) {
        state.editState.authZConfig.aaaServerProfiles[1] = {
          aaaServerProfileId: action.payload,
        };
      }
    },

    // cert config
    setCertConfig: (state, action: PayloadAction<{ value: EdgeIpsecConfig; index: number }>) => {
      if (!state.editState.edgeIpsecConfig) {
        state.editState.edgeIpsecConfig = [];
      }
      state.editState.edgeIpsecConfig[action.payload.index] = action.payload.value;
    },

    addNewCertConfig: (state) => {
      const defaultCert: EdgeIpsecConfig = {
        edgeIpsecCert: "",
        edgeIpsecCertFileName: "",
        edgeIpsecKey: "",
        edgeIpsecKeyFileName: "",
      };
      if (!state.editState.edgeIpsecConfig) {
        state.editState.edgeIpsecConfig = [];
      }
      state.editState.edgeIpsecConfig.push(defaultCert);
    },

    setIPSECcertificate: (
      state,
      action: PayloadAction<{
        value: {
          name: string;
          content: string;
        };
        index: number;
      }>
    ) => {
      if (!state.editState.edgeIpsecConfig) {
        state.editState.edgeIpsecConfig = [];
      }
      // Ensure the index exists in the array
      while (state.editState.edgeIpsecConfig.length <= action.payload.index) {
        state.editState.edgeIpsecConfig.push({
          edgeIpsecCert: "",
          edgeIpsecCertFileName: "",
          edgeIpsecKey: "",
          edgeIpsecKeyFileName: "",
        });
      }
      state.editState.edgeIpsecConfig[action.payload.index].edgeIpsecCert = action.payload.value.content;
      state.editState.edgeIpsecConfig[action.payload.index].edgeIpsecCertFileName = action.payload.value.name;
    },

    setIPSECkey: (
      state,
      action: PayloadAction<{
        value: {
          name: string;
          content: string;
        };
        index: number;
      }>
    ) => {
      if (!state.editState.edgeIpsecConfig) {
        state.editState.edgeIpsecConfig = [];
      }
      // Ensure the index exists in the array
      while (state.editState.edgeIpsecConfig.length <= action.payload.index) {
        state.editState.edgeIpsecConfig.push({
          edgeIpsecCert: "",
          edgeIpsecCertFileName: "",
          edgeIpsecKey: "",
          edgeIpsecKeyFileName: "",
        });
      }
      state.editState.edgeIpsecConfig[action.payload.index].edgeIpsecKey = action.payload.value.content;
      state.editState.edgeIpsecConfig[action.payload.index].edgeIpsecKeyFileName = action.payload.value.name;
    },


    removeCertConfig: (state, action: PayloadAction<number>) => {
      state.editState.edgeIpsecConfig = state.editState.edgeIpsecConfig?.filter((_, i) => action.payload !== i);
    },
    setIsAutoAssignCert: (state, action: PayloadAction<boolean>) => {
      state.editState.isAutoAssignCert = action.payload;
    },

    // setEdgeLocationType: (state, action: PayloadAction<string>) => {
    //     state.editState.location.locationType = action.payload;
    // },
    setLocationName: (state, action: PayloadAction<string>) => {
      state.editState.location.name = action.payload;
    },
    setLatitude: (state, action: PayloadAction<number>) => {
      state.editState.location.latitude = action.payload;
    },
    setLongitude: (state, action: PayloadAction<number>) => {
      state.editState.location.longitude = action.payload;
    },

    // core config
    addPlmn: (state) => {
      if (!state.editState.coreConfig) {
        state.editState.coreConfig = { plmnList: [] };
      }
      if (!state.editState.coreConfig.plmnList) {
        state.editState.coreConfig.plmnList = [];
      }
      state.editState.coreConfig.plmnList.push({ mcc: "", mnc: "" });
    },
    removePlmn: (state, action: PayloadAction<number>) => {
      if (state.editState.coreConfig?.plmnList) {
        state.editState.coreConfig.plmnList = state.editState.coreConfig.plmnList.filter((_, i) => i !== action.payload);
      }
    },
    updatePlmn: (state, action: PayloadAction<{ index: number; field: "mcc" | "mnc"; value: string }>) => {
      if (state.editState.coreConfig?.plmnList && state.editState.coreConfig.plmnList[action.payload.index]) {
        state.editState.coreConfig.plmnList[action.payload.index][action.payload.field] = action.payload.value;
      }
    },
    addSnssai: (state) => {
      if (!state.editState.coreConfig) {
        state.editState.coreConfig = { plmnList: [] };
      }
      if (!state.editState.coreConfig.snssai) {
        state.editState.coreConfig.snssai = [];
      }
      state.editState.coreConfig.snssai.push({ sst: DEFAULT_SNSSAI_SST, sd: [] });
    },
    removeSnssai: (state, action: PayloadAction<number>) => {
      if (state.editState.coreConfig?.snssai) {
        state.editState.coreConfig.snssai = state.editState.coreConfig.snssai.filter((_, i) => i !== action.payload);
      }
    },
    updateSnssai: (state, action: PayloadAction<{ index: number; snssai: SNSSAI }>) => {
      if (state.editState.coreConfig?.snssai && state.editState.coreConfig?.snssai[action.payload.index]) {
        state.editState.coreConfig.snssai[action.payload.index] = action.payload.snssai;
      }
    },

  },
});

export const edgeEditActions = edgeEditSlice.actions;
export default edgeEditSlice.reducer;
export const EdgeEditState = (state: RootState) => state.edgeEdit.editState;
export const EdgeEditSecondaryAAA = (state: RootState) => state.edgeEdit.isSecondaryAAA;
export const selectIsNetworkModified = (index: number) => (state: RootState) => !!state.edgeEdit.isNetworkModified[index];
export const selectIsFabricNetworkModified = (fabricIndex: number, networkIndex: number) => (state: RootState) =>
  !!state.edgeEdit.isFabricNetworkModified[`${fabricIndex}-${networkIndex}`];

// export const EdgeEditIsValid = (state: RootState) => state.edgeEdit.isValid;
